/**
 * Why a Visit was skipped, as a picklist so attendance can group by reason. Values are stored on the
 * Visit; renaming one orphans every row that carries it.
 */
export enum VisitSkipReason {
    CustomerRequest = 'CustomerRequest',
    SiteInaccessible = 'SiteInaccessible',
    WeatherUnsafe = 'WeatherUnsafe',
    NoLongerRequired = 'NoLongerRequired',
    Duplicate = 'Duplicate',
    Other = 'Other',
}

export const VISIT_SKIP_REASON_LABELS: Readonly<Record<VisitSkipReason, string>> = {
    [VisitSkipReason.CustomerRequest]: 'Customer request',
    [VisitSkipReason.SiteInaccessible]: 'Site inaccessible',
    [VisitSkipReason.WeatherUnsafe]: 'Weather or unsafe conditions',
    [VisitSkipReason.NoLongerRequired]: 'No longer required',
    [VisitSkipReason.Duplicate]: 'Duplicate',
    [VisitSkipReason.Other]: 'Other',
};

/** The Skip command payload. `reason` alone is the pre-picklist shape and still accepted. */
export interface VisitSkipRequest {
    id: string;
    reasonCode?: VisitSkipReason;
    reason?: string;
}

export interface VisitSkipReasonAnswer {
    code: VisitSkipReason;
    text?: string;
}

export type VisitSkipReasonResolution = { answer: VisitSkipReasonAnswer; error?: undefined } | { answer?: undefined; error: string };

export function isVisitSkipReason(value: unknown): value is VisitSkipReason {

    return Object.values(VisitSkipReason).includes(value as VisitSkipReason);
}

/** The picklist code a skipped Visit carries; a pre-picklist free-text skip reads as Other, as the Skip command does. */
export function visitSkipReasonOf(visit: { status?: string; data?: { skipReasonCode?: unknown; skipReason?: unknown } | null }): VisitSkipReason | undefined {

    if (visit.status !== 'Skipped') return undefined;
    const code = visit.data?.skipReasonCode;
    if (isVisitSkipReason(code)) return code;

    return visit.data?.skipReason ? VisitSkipReason.Other : undefined;
}

/**
 * Validates a skip answer. Free text with no code is a caller from before the picklist and reads
 * as Other, so older clients keep working and still land in a reportable bucket.
 */
export function resolveVisitSkipReason(reasonCode: unknown, reason: unknown): VisitSkipReasonResolution {

    const text = typeof reason === 'string' ? reason.trim() || undefined : undefined;
    if (reasonCode == null || reasonCode === '') {
        return text ? { answer: { code: VisitSkipReason.Other, text } } : { error: 'Skip reason is required' };
    }
    if (!isVisitSkipReason(reasonCode)) return { error: `Unknown skip reason: ${String(reasonCode)}` };
    if (reasonCode === VisitSkipReason.Other && !text) return { error: 'Describe the reason for Other' };

    return { answer: text ? { code: reasonCode, text } : { code: reasonCode } };
}

/** One line for activity notes and the legacy `skipReason` text readers still show. */
export function visitSkipReasonNote(answer: VisitSkipReasonAnswer): string {

    if (answer.code === VisitSkipReason.Other) return answer.text ?? VISIT_SKIP_REASON_LABELS.Other;

    return answer.text ? `${VISIT_SKIP_REASON_LABELS[answer.code]}: ${answer.text}` : VISIT_SKIP_REASON_LABELS[answer.code];
}
